/**
 * Capacitor native bridge initialization.
 * Only activates when running inside a native iOS/Android shell.
 * Safe to import in web — all calls are no-ops when Capacitor isn't present.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

export async function initNativeBridge() {
  if (!isNative) return;

  // Status bar — light text over dark background
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0a0a1a' });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (e) {
    // Status bar plugin not available
  }

  // Splash screen — auto-hide after app renders
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    // Splash screen plugin not available
  }

  // Handle deep links (e.g., newlogicalsports://news/some-slug)
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url);
      const path = url.pathname;
      if (path) {
        window.location.hash = '';
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    });

    // Handle back button on iOS (swipe gesture already native)
    App.addListener('backButton', () => {
      if (window.history.length > 1) {
        window.history.back();
      }
    });
  } catch (e) {
    // App plugin not available
  }

  // Keyboard handling for iOS
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });
  } catch (e) {
    // Keyboard plugin not available
  }

  console.log(`[Capacitor] Native bridge initialized — platform: ${platform}`);
}
